import { useMemo, useState } from 'react';
import type {
  DailyPurchaseDefault,
  DailyPurchaseGroup,
  Dish,
  FixedPurchaseOccurrence,
  Meal,
  PlanDay,
  PlanSelection,
  WeeklyPlan,
} from '../types';
import { MEAL_LABELS } from '../types';
import { dailyPurchaseDefaultAppliesToDate } from '../lib/calculation';
import { formatShortDate, weekdayLabel } from '../lib/date';
import { ChevronDownIcon, PlusIcon, UtensilsIcon, XIcon } from './Icons';
import { BatchAdjustModal } from './BatchAdjustModal';
import { DishSelector } from './DishSelector';

interface Props {
  plan: WeeklyPlan;
  dishes: Dish[];
  dailyPurchaseDefaults?: DailyPurchaseDefault[];
  dailyPurchaseGroups?: DailyPurchaseGroup[];
  onChange: (plan: WeeklyPlan) => void;
}

interface SelectorContext {
  date: string;
  meal: Meal;
}

interface BatchEditorContext {
  day: PlanDay;
  selection: PlanSelection;
  dishName: string;
}

function serviceFor(day: PlanDay, meal: Meal): boolean {
  return day.mealService?.[meal] ?? day.serviceEnabled;
}

function fixedPurchasesForDay(
  date: string,
  defaults: DailyPurchaseDefault[],
  groups: DailyPurchaseGroup[],
  resolved?: FixedPurchaseOccurrence[],
): Array<{
  name: string;
  quantity: string;
  unit: string;
  adjusted: boolean;
}> {
  if (resolved) {
    return resolved.filter((item) => item.date === date).map((item) => ({
      name: item.name,
      quantity: item.sourceType === 'dish' ? String(item.batchCount ?? 1) : String(item.quantity ?? ''),
      unit: item.sourceType === 'dish' ? '批' : String(item.unit ?? ''),
      adjusted: item.adjusted,
    }));
  }
  const group = groups.find((candidate) => candidate.date === date);
  return defaults
    .filter((item) => dailyPurchaseDefaultAppliesToDate(item, date))
    .map((item) => {
      const total = group?.items.find((candidate) => (
        candidate.name === item.name
        && candidate.unit === item.unit
        && (
          !candidate.sources?.length
          || candidate.sources.some((source) => source.kind === 'daily_default')
        )
      ));
      return {
        name: item.name,
        quantity: total?.finalQuantity ?? item.quantity,
        unit: item.unit,
        adjusted: Boolean(total && total.finalQuantity !== total.calculatedQuantity),
      };
    });
}

export function WeekPlanner({
  plan,
  dishes,
  dailyPurchaseDefaults = [],
  dailyPurchaseGroups = [],
  onChange,
}: Props) {
  const [selector, setSelector] = useState<SelectorContext | null>(null);
  const [batchEditor, setBatchEditor] = useState<BatchEditorContext | null>(null);
  const activeDishes = useMemo(() => dishes.filter((dish) => dish.active), [dishes]);
  const resolvedDailyFixed = (plan.fixedPurchaseRulesRevision ?? 0) > 0
    ? plan.resolvedFixedPurchases?.daily
    : undefined;
  const hasFixedPurchaseSystem = Boolean(
    dailyPurchaseDefaults.length
    || resolvedDailyFixed
    || plan.resolvedFixedPurchases?.skipped.length,
  );

  const changeDay = (date: string, transform: (day: PlanDay) => PlanDay) => {
    onChange({ ...plan, status: 'draft', days: plan.days.map((day) => day.date === date ? transform(day) : day) });
  };

  const replaceMealSelections = (date: string, meal: Meal, selections: PlanSelection[]) => {
    changeDay(date, (current) => ({
      ...current,
      selections: [...current.selections.filter((item) => item.meal !== meal), ...selections],
    }));
  };

  const setBatchCount = (day: PlanDay, selection: PlanSelection, batchCount: number) => {
    const safe = Math.min(99, Math.max(1, Math.round(batchCount)));
    changeDay(day.date, (current) => ({
      ...current,
      selections: current.selections.map((item) => item.meal === selection.meal && item.dishId === selection.dishId ? { ...item, batchCount: safe } : item),
    }));
  };

  const removeSelection = (day: PlanDay, selection: PlanSelection) => {
    changeDay(day.date, (current) => ({
      ...current,
      selections: current.selections.filter((item) => !(item.meal === selection.meal && item.dishId === selection.dishId)),
    }));
  };

  if (!activeDishes.length) {
    return (
      <div className="empty-state planner-empty">
        <UtensilsIcon size={36} />
        <h3>菜品库暂时为空</h3>
        <p>请先打开菜品库，添加菜品和固定食材用量。</p>
      </div>
    );
  }

  const selectorDay = selector ? plan.days.find((day) => day.date === selector.date) : undefined;
  const selectorSelections = selectorDay && selector
    ? selectorDay.selections.filter((selection) => selection.meal === selector.meal)
    : [];

  return (
    <>
      <div className="week-days">
        {plan.days.map((day) => {
          const breakfastEnabled = serviceFor(day, 'breakfast');
          const lunchEnabled = serviceFor(day, 'lunch');
          const allStopped = !breakfastEnabled && !lunchEnabled;
          const selectionCount = day.selections.filter((selection) => serviceFor(day, selection.meal)).length;
          const fixedPurchases = fixedPurchasesForDay(
            day.date,
            dailyPurchaseDefaults,
            dailyPurchaseGroups,
            resolvedDailyFixed,
          );

          return (
            <article className={`day-card ${allStopped ? 'day-card--rest' : ''}`} key={day.date}>
              <header className="day-card__header day-card__header--read-only">
                <div className="day-card__summary">
                  <span className={`day-card__date ${allStopped ? 'is-muted' : ''}`}>
                    <strong>{formatShortDate(day.date)}</strong>
                    <small>{weekdayLabel(day.date)}</small>
                  </span>
                  <span className="day-card__state">
                    {allStopped ? (
                      <><span className="rest-pill">全天停餐</span>{day.label && <small>{day.label}</small>}</>
                    ) : (
                      <><span className="status-dot" />{selectionCount ? `已选 ${selectionCount} 道菜` : '等待选菜'}</>
                    )}
                  </span>
                </div>
              </header>

              {hasFixedPurchaseSystem && (
                <div
                  className={`inline-alert day-card__fixed-purchases ${allStopped ? 'day-card__fixed-purchases--off' : ''}`}
                  aria-label={allStopped ? '当天固定采购不计入' : '当天固定采购'}
                >
                  {allStopped ? (
                    <span className="extra-purchase-row__copy"><strong>固定采购不计入</strong><small>当天停餐，系统不会加入固定食材</small></span>
                  ) : fixedPurchases.length > 0 ? (
                    <span className="extra-purchase-row__copy">
                      <strong>当天固定采购已自动加入</strong>
                      <small>
                        {fixedPurchases.map((item) => (
                          `${item.name} ${item.quantity}${item.unit}${item.adjusted ? '（已调整）' : ''}`
                        )).join('、')}
                      </small>
                    </span>
                  ) : (
                    <span className="extra-purchase-row__copy"><strong>当天没有固定采购</strong><small>只汇总已选择菜品的食材</small></span>
                  )}
                </div>
              )}

              {!allStopped && (
                <div className="day-card__content">
                  {(['breakfast', 'lunch'] as Meal[]).map((meal) => {
                    const mealEnabled = serviceFor(day, meal);
                    const mealSelections = day.selections.filter((item) => item.meal === meal);
                    if (!mealEnabled) {
                      return (
                        <section className="meal-block meal-block--off" key={meal} aria-label={`${MEAL_LABELS[meal]}停餐`}>
                          <div className="meal-block__title"><span>{MEAL_LABELS[meal]}</span></div>
                          <div className="meal-stop-state"><strong>停餐</strong><small>可在“本周供餐设置”中调整</small></div>
                        </section>
                      );
                    }
                    return (
                      <section className="meal-block" key={meal} aria-label={MEAL_LABELS[meal]}>
                        <div className="meal-block__title">
                          <span>{MEAL_LABELS[meal]}</span>
                          <small>{mealSelections.length ? `已选 ${mealSelections.length} 道` : '可不选'}</small>
                        </div>
                        <div className="meal-selection">
                          {mealSelections.length > 0 && (
                            <div className="meal-selection__toolbar">
                              <span>每道菜默认 1 批</span>
                              <button
                                className="button button--soft button--small"
                                type="button"
                                aria-label={`为${day.date}${MEAL_LABELS[meal]}继续选择菜品`}
                                onClick={() => setSelector({ date: day.date, meal })}
                              >
                                <PlusIcon size={16} />继续选菜
                              </button>
                            </div>
                          )}

                          {mealSelections.length > 0 ? (
                            <div className="batch-list">
                              {mealSelections.map((selection) => {
                                const dish = dishes.find((item) => item.id === selection.dishId);
                                const name = selection.recipeSnapshot?.dishName ?? dish?.name ?? '已停用菜品';
                                return (
                                  <div className="batch-row" key={`${meal}-${selection.dishId}`}>
                                    <span className="batch-row__name"><strong>{name}</strong><small>{selection.batchCount * 50} 人用量</small></span>
                                    <div className="batch-row__actions">
                                      <button className="batch-row__batch" type="button" onClick={() => setBatchEditor({ day, selection, dishName: name })} aria-label={`调整${name}批次数量`}>
                                        {selection.batchCount}批<span>›</span>
                                      </button>
                                      <button className="icon-button icon-button--danger batch-row__remove" type="button" onClick={() => removeSelection(day, selection)} aria-label={`移除${name}`}><XIcon size={16} /></button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <button
                              className="meal-selection__empty"
                              type="button"
                              aria-label={`为${day.date}${MEAL_LABELS[meal]}选择菜品`}
                              onClick={() => setSelector({ date: day.date, meal })}
                            >
                              <UtensilsIcon size={18} />
                              <span><strong>选择菜品</strong><small>支持搜索、分类和多选</small></span>
                              <ChevronDownIcon size={16} />
                            </button>
                          )}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {selector && selectorDay && (
        <DishSelector
          open
          dateLabel={`${formatShortDate(selectorDay.date)} ${weekdayLabel(selectorDay.date)}`}
          meal={selector.meal}
          mealLabel={MEAL_LABELS[selector.meal]}
          dishes={dishes}
          selections={selectorSelections}
          onClose={() => setSelector(null)}
          onApply={(selections) => replaceMealSelections(selector.date, selector.meal, selections)}
        />
      )}
      {batchEditor && (
        <BatchAdjustModal
          open
          dishName={batchEditor.dishName}
          value={batchEditor.selection.batchCount}
          onClose={() => setBatchEditor(null)}
          onApply={(value) => setBatchCount(batchEditor.day, batchEditor.selection, value)}
        />
      )}
    </>
  );
}
